import {
  Button,
  IconButton,
  SelectInput,
  SelectInputItem,
  Switch,
  TextField,
} from '@ads/components-react';
import { XMarkIcon } from '@heroicons/react/24/outline';
import { zodResolver } from '@hookform/resolvers/zod';
import * as Dialog from '@radix-ui/react-dialog';
import dayjs from 'dayjs';
import { useEffect, useMemo, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import type { PublishBotExamShowViewModel } from '@/shared/api/generated/types/PublishBotExamShowViewModel';
import type { PublishBotExamViewModel } from '@/shared/api/generated/types/PublishBotExamViewModel';
import { DatePicker, Slider, Spinner } from '@/shared/ui/atoms';
import { createPublishFormSchema, type PublishFormValues } from './publishFormSchema';

const DEFAULT_VALUES: PublishFormValues = {
  startDate: '',
  endDate: '',
  weight: 1,
  randomize: true,
  percentualComplexity: 0,
  tries: 3,
  time: 25,
  enableSafeEvaluationMode: false,
  teacherEvaluationExamId: null,
};

export interface PublishModalProps {
  /** Controla a visibilidade do modal. */
  readonly open: boolean;
  /** ID da prova bot sendo publicada (compõe o payload). */
  readonly botExamId: number;
  /** Dados de prévia (`GET /publish`) para pré-preencher título, datas e professores. */
  readonly show: PublishBotExamShowViewModel | null;
  /** Exibe o estado de carregamento da prévia. */
  readonly isLoadingShow?: boolean;
  /** Exibe spinner no botão de confirmação enquanto publica. */
  readonly isPublishing?: boolean;
  /** Callback ao fechar sem confirmar (overlay, Escape, cancelar ou X). */
  readonly onClose: () => void;
  /** Callback ao confirmar, com o `PublishBotExamViewModel` completo. */
  readonly onConfirm: (data: PublishBotExamViewModel) => void;
}

/**
 * Modal de configuração e publicação de uma prova em revisão.
 *
 * Renderiza um formulário (React Hook Form + Zod, schema derivado do gerado pelo
 * Kubb) com os campos de `PublishBotExamViewModel` (período, peso, randomização,
 * complexidade, tentativas, tempo, modo de avaliação segura e professor),
 * pré-preenchendo título, datas e professores a partir do `PublishBotExamShowViewModel`.
 *
 * @param open - Controla a visibilidade do modal
 * @param botExamId - ID da prova bot sendo publicada
 * @param show - Dados de prévia para prefill
 * @param isLoadingShow - Estado de carregamento da prévia
 * @param isPublishing - Estado de submissão da publicação
 * @param onClose - Callback ao fechar sem confirmar
 * @param onConfirm - Callback ao confirmar, com o modelo de publicação
 *
 * @example
 * ```tsx
 * <PublishModal
 *   open={isOpen}
 *   botExamId={item.id}
 *   show={show}
 *   isPublishing={isPublishing}
 *   onClose={close}
 *   onConfirm={publish}
 * />
 * ```
 */
export function PublishModal({
  open,
  botExamId,
  show,
  isLoadingShow = false,
  isPublishing = false,
  onClose,
  onConfirm,
}: PublishModalProps) {
  const { t } = useTranslation('examRevision');
  // Elemento do Dialog.Content usado como container do portal do calendário —
  // mantém o popover "dentro" do modal, evitando o fechamento ao selecionar data.
  const [contentEl, setContentEl] = useState<HTMLDivElement | null>(null);

  const {
    control,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<PublishFormValues>({
    resolver: zodResolver(useMemo(() => createPublishFormSchema(t), [t])),
    defaultValues: DEFAULT_VALUES,
  });

  useEffect(() => {
    if (!show) return;
    reset({
      ...DEFAULT_VALUES,
      startDate: show.startDate ? dayjs(show.startDate).format('YYYY-MM-DD') : '',
      endDate: show.endDate ? dayjs(show.endDate).format('YYYY-MM-DD') : '',
    });
  }, [show, reset]);

  const submit = handleSubmit(values => {
    onConfirm({
      botExamId,
      startDate: values.startDate ? dayjs(values.startDate).toISOString() : null,
      endDate: values.endDate ? dayjs(values.endDate).toISOString() : null,
      weight: values.weight,
      randomize: values.randomize,
      percentualComplexity: values.percentualComplexity,
      enableSafeEvaluationMode: values.enableSafeEvaluationMode,
      tries: values.tries,
      time: values.time,
      teacherEvaluationExamId: values.teacherEvaluationExamId,
    });
  });

  const teachers = show?.teachersList ?? [];

  return (
    <Dialog.Root open={open} onOpenChange={o => !o && onClose()}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-none z-40 h-screen w-screen bg-base-gray-900 bg-opacity-medium backdrop-blur-soft" />
        <Dialog.Content
          ref={setContentEl}
          className="z-50 fixed left-1/2 top-1/2 flex max-h-[85vh] w-full max-w-2xl -translate-x-1/2 -translate-y-1/2 flex-col rounded-md bg-ctx-layout-surface shadow-level3 focus:outline-none"
          aria-describedby={undefined}
          onInteractOutside={event => {
            // O calendário do DatePicker é renderizado em um portal (fora do
            // Dialog.Content). Sem este guard, clicar em uma data conta como
            // "interação externa" e o Radix fecha o modal. Ignora interações que
            // partem de qualquer popover/calendário portado.
            const target = event.detail.originalEvent.target as HTMLElement | null;
            if (target?.closest('[data-radix-popper-content-wrapper]')) {
              event.preventDefault();
            }
          }}
        >
          {/* Header */}
          <div className="flex items-center justify-between border-b-hairline border-ctx-layout-border px-md py-xs">
            <div className="flex flex-col gap-quark">
              <Dialog.Title className="ts-subtitle-sm text-ctx-content-title">
                {t('publish.modalTitle')}
              </Dialog.Title>
              {show?.title && (
                <span className="ts-caption-xxs text-ctx-content-placeholder">
                  {t('publish.modalSubtitle', { title: show.title })}
                </span>
              )}
            </div>
            <Dialog.Close asChild>
              <IconButton
                icon={XMarkIcon}
                hierarchy="tertiary"
                size="sm"
                ariaLabel={t('publish.actions.cancel')}
              />
            </Dialog.Close>
          </div>

          {/* Body */}
          {isLoadingShow ? (
            <div className="flex items-center justify-center px-md py-xl">
              <Spinner size="md" />
            </div>
          ) : (
            <form onSubmit={submit} className="flex flex-col overflow-hidden">
              <div className="flex flex-col gap-mini overflow-y-auto px-md py-md at-scrollbar">
                {/* Período */}
                <span className="ts-subtitle-xxs text-ctx-content-title">
                  {t('publish.form.period')}
                </span>
                <div className="grid grid-cols-2 gap-md">
                  <Controller
                    control={control}
                    name="startDate"
                    render={({ field }) => (
                      <DatePickerField
                        label={t('publish.form.startDate')}
                        value={field.value}
                        onChange={field.onChange}
                        error={errors.startDate?.message}
                        portalContainer={contentEl}
                      />
                    )}
                  />
                  <Controller
                    control={control}
                    name="endDate"
                    render={({ field }) => (
                      <DatePickerField
                        label={t('publish.form.endDate')}
                        value={field.value}
                        onChange={field.onChange}
                        error={errors.endDate?.message}
                        portalContainer={contentEl}
                      />
                    )}
                  />
                </div>

                {/* Números */}
                <div className="grid grid-cols-2 gap-xxs">
                  <Controller
                    control={control}
                    name="weight"
                    render={({ field }) => (
                      <NumberField
                        label={t('publish.form.weight')}
                        value={field.value}
                        onChange={field.onChange}
                        error={errors.weight?.message}
                      />
                    )}
                  />
                  <Controller
                    control={control}
                    name="tries"
                    render={({ field }) => (
                      <NumberField
                        label={t('publish.form.tries')}
                        value={field.value}
                        onChange={field.onChange}
                        error={errors.tries?.message}
                      />
                    )}
                  />
                  <Controller
                    control={control}
                    name="time"
                    render={({ field }) => (
                      <NumberField
                        label={t('publish.form.time')}
                        value={field.value}
                        onChange={field.onChange}
                        error={errors.time?.message}
                        helper={t('publish.form.timeHint')}
                      />
                    )}
                  />
                </div>
                {/* Percentual de complexidade (range 0–100) */}
                <Controller
                  control={control}
                  name="percentualComplexity"
                  render={({ field }) => (
                    <ComplexityField
                      label={t('publish.form.complexity')}
                      value={field.value}
                      onChange={field.onChange}
                      error={errors.percentualComplexity?.message}
                    />
                  )}
                />

                {/* Professor */}
                <Controller
                  control={control}
                  name="teacherEvaluationExamId"
                  render={({ field }) => (
                    <SelectInput
                      label={t('publish.form.teacher')}
                      placeholder={t('publish.form.teacherPlaceholder')}
                      value={field.value != null ? String(field.value) : ''}
                      onValueChange={v => field.onChange(v === '' ? null : Number(v))}
                      size="md"
                      fullWidth
                      disabled={teachers.length === 0}
                    >
                      {teachers.map(teacher => (
                        <SelectInputItem
                          key={teacher.key}
                          value={String(teacher.key)}
                          custom={{ root: 'text-nowrap' }}
                        >
                          {teacher.value}
                        </SelectInputItem>
                      ))}
                    </SelectInput>
                  )}
                />

                {/* Toggles */}
                <div className="flex items-center justify-between rounded-sm border-hairline border-ctx-layout-border px-mini py-nano">
                  <span className="ts-paragraph-xs text-ctx-content-title">
                    {t('publish.form.randomize')}
                  </span>
                  <Controller
                    control={control}
                    name="randomize"
                    render={({ field }) => (
                      <Switch checked={field.value} onCheckedChange={field.onChange} size="md" />
                    )}
                  />
                </div>
                <div className="flex items-center justify-between rounded-sm border-hairline border-ctx-layout-border px-mini py-nano">
                  <span className="ts-paragraph-xs text-ctx-content-title">
                    {t('publish.form.safeMode')}
                  </span>
                  <Controller
                    control={control}
                    name="enableSafeEvaluationMode"
                    render={({ field }) => (
                      <Switch checked={field.value} onCheckedChange={field.onChange} size="md" />
                    )}
                  />
                </div>
              </div>

              {/* Actions */}
              <div className="flex items-center justify-end gap-mini border-t-hairline border-ctx-layout-border px-md py-xs">
                <Button
                  type="button"
                  hierarchy="secondary"
                  size="md"
                  onClick={onClose}
                  disabled={isPublishing}
                >
                  {t('publish.actions.cancel')}
                </Button>
                <Button type="submit" hierarchy="primary" size="md" isLoading={isPublishing}>
                  {t('publish.actions.confirm')}
                </Button>
              </div>
            </form>
          )}
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}

/** Campo de data do formulário de publicação (wrapper do `DatePicker` compartilhado). */
function DatePickerField({
  label,
  value,
  onChange,
  error,
  portalContainer,
}: {
  readonly label: string;
  readonly value: string;
  readonly onChange: (iso: string) => void;
  readonly error: string | undefined;
  readonly portalContainer?: HTMLElement | null;
}) {
  return (
    <DatePicker
      label={label}
      value={value}
      onChange={onChange}
      hasError={Boolean(error)}
      portalContainer={portalContainer}
      {...(error ? { helper: error } : {})}
    />
  );
}

/** Campo numérico do formulário de publicação (wrapper do `TextField` do ADS). */
function NumberField({
  label,
  value,
  onChange,
  error,
  helper,
}: {
  readonly label: string;
  readonly value: number;
  readonly onChange: (value: number) => void;
  readonly error: string | undefined;
  readonly helper?: string;
}) {
  const helperText = error ?? helper;
  return (
    <TextField
      custom={{ input: '!w-full' }}
      label={label}
      type="number"
      size="md"
      min={0}
      value={String(value)}
      onChange={e => onChange(Number(e.target.value))}
      hasError={Boolean(error)}
      {...(helperText ? { helper: helperText } : {})}
    />
  );
}

/** Campo de percentual de complexidade como slider (0–100) do formulário de publicação. */
function ComplexityField({
  label,
  value,
  onChange,
  error,
}: {
  readonly label: string;
  readonly value: number;
  readonly onChange: (value: number) => void;
  readonly error: string | undefined;
}) {
  return (
    <div className="flex flex-col gap-quark">
      <div className="flex items-center justify-between">
        <span className="ts-heading-xxs !font-medium text-ctx-content-title">{label}</span>
        <span className="ts-paragraph-xs text-ctx-content-base">{value}%</span>
      </div>
      <Slider
        min={0}
        max={100}
        value={value}
        onChange={e => onChange(Number(e.target.value))}
        aria-label={label}
      />
      {error && <span className="ts-paragraph-xxs text-base-danger-500">{error}</span>}
    </div>
  );
}
